
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Download, Save, Calendar, Clock, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ConsultationNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: any;
  psychologistId: string;
}

const ConsultationNotesModal = ({ isOpen, onClose, appointment, psychologistId }: ConsultationNotesModalProps) => {
  const [notes, setNotes] = useState("");
  const [originalNotes, setOriginalNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen && appointment) {
      loadNotes();
    }
  }, [isOpen, appointment]);

  useEffect(() => {
    setHasChanges(notes !== originalNotes);
  }, [notes, originalNotes]);

  const loadNotes = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('consultation_notes')
        .select('*')
        .eq('appointment_id', appointment.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      const notesText = data?.notes || "";
      setNotes(notesText);
      setOriginalNotes(notesText);
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Erro ao carregar anotações');
    } finally {
      setIsLoading(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data: existingNote } = await supabase
        .from('consultation_notes')
        .select('id')
        .eq('appointment_id', appointment.id)
        .single();

      if (existingNote) {
        // Update existing note
        const { error } = await supabase
          .from('consultation_notes')
          .update({
            notes,
            updated_at: new Date().toISOString()
          })
          .eq('appointment_id', appointment.id);

        if (error) throw error;
      } else {
        // Create new note
        const { error } = await supabase
          .from('consultation_notes')
          .insert({
            psychologist_id: psychologistId,
            appointment_id: appointment.id,
            patient_id: appointment.user_id,
            notes
          });

        if (error) throw error;
      }

      setOriginalNotes(notes);
      toast.success('Anotações salvas com sucesso');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Erro ao salvar anotações');
    } finally {
      setSaving(false);
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('export-consultation-notes-pdf', {
        body: { appointmentId: appointment.id }
      });

      if (error) throw error;

      // Create download link
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `prontuario_${appointment.profiles?.first_name || 'paciente'}_${format(new Date(appointment.date), 'dd-MM-yyyy')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF exportado com sucesso');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setIsExporting(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Prontuário da Consulta
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Appointment Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informações da Consulta</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Paciente</p>
                    <p className="font-medium">
                      {appointment.profiles?.first_name} {appointment.profiles?.last_name}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Data</p>
                    <p className="font-medium">
                      {format(new Date(appointment.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Horário</p>
                    <p className="font-medium">
                      {appointment.time} ({appointment.duration} min)
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <Badge variant={appointment.status === 'completed' ? 'default' : 'secondary'}>
                  {appointment.status === 'completed' ? 'Concluída' : 
                   appointment.status === 'confirmed' ? 'Confirmada' : 'Pendente'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Notes Editor */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Anotações da Consulta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  <Textarea
                    placeholder="Digite suas anotações sobre a consulta..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-[200px] resize-none"
                  />
                  
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted-foreground">
                      {notes.length} caracteres
                      {hasChanges && " • Alterações não salvas"}
                    </p>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={saveNotes}
                        disabled={isSaving || !hasChanges}
                        variant="default"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {isSaving ? 'Salvando...' : 'Salvar'}
                      </Button>
                      
                      <Button
                        onClick={exportToPDF}
                        disabled={isExporting || !originalNotes}
                        variant="outline"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {isExporting ? 'Exportando...' : 'Exportar PDF'}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Confidentiality Notice */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <p className="text-sm text-amber-800">
                <strong>Confidencialidade:</strong> Estas anotações são confidenciais e protegidas por sigilo profissional.
                Mantenha a segurança e privacidade dos dados do paciente em conformidade com o código de ética profissional.
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationNotesModal;
